// pages/VizLoaderPage.tsx
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from '../../../../Flux/index.js'
import type { AppDispatch } from '../../../../Flux/index.js'
import type { State } from '../../../../Flux/Slice/index.js'
import { Actions } from '../../../../Flux/Slice/index.js'
import { projectSlice } from '../../../../Flux/Slice/Project/ProjectSlice.js'
import VizWorkspace from './VizWorkspace.js'
import { canUseProjects, canUseSavedViews } from '../../../../Model/index.js'
import { useGetAuthenticatedUser } from '../../../../Flux/Selector/index.js'
import { trackProjectVizOpened } from '../../../../Services/Telemetry/index.js'
import { ScopeNotFoundNotice } from '../../ScopeNotFoundNotice.js'
import { useWorkspaceHydration } from './UseWorkspaceHydration.js'
import { useScopeLoad } from './UseScopeLoad.js'

function requestProject(dispatch: AppDispatch, projectId: string) {
  dispatch(Actions[projectSlice.name].getProjectRequest({ projectId }))
  dispatch(Actions[projectSlice.name].getParticipantsRequest({ projectId }))
  dispatch(Actions[projectSlice.name].getProjectSamplesRequest({ projectId }))
  dispatch(Actions[projectSlice.name].loadProjectOrdersRequest({ projectId }))
}

/**
 * Project-scoped entry point for the visualization workspace
 */
export default function VizLoaderPage() {
  const { id } = useParams<{ id: string }>()

  const user = useGetAuthenticatedUser()
  const projectsAccess = canUseProjects(user)
  const savedViewsAccess = canUseSavedViews(user)

  const projectNotFound = useSelector((s: typeof State) => !!id && s.project.notFoundProjectId === id)

  /* Workspace reach for the project scope, counted on arrival — the order workspace is counted at
   * the context menu instead, so group `viz.opened` by `scope`/`trigger` before comparing them. */
  useEffect(() => {
    if (id) trackProjectVizOpened('workspace', id)
  }, [id])

  const loaded = useScopeLoad('project', id, requestProject)
  const projectReady = loaded.scope
  const samplesReady = loaded.samples
  const ordersReady = loaded.orders

  const allReady = projectReady && samplesReady && ordersReady

  const { hydrated, resumable } = useWorkspaceHydration(id, allReady, savedViewsAccess)

  if (user && !projectsAccess) {
    return <div className="p-6">You do not have access to this feature.</div>
  }

  if (!id) {
    return <div className="p-6">No project id in URL.</div>
  }

  if (projectNotFound) {
    return (
      <div className="p-6">
        <ScopeNotFoundNotice scopeKind="project" />
      </div>
    )
  }

  // Once hydrated, a re-fetch of the scope's own data (a reconnect, a lock change) must not unmount the workspace.
  if (!allReady && !hydrated) {
    return (
      <div className="p-8">
        <h1 className="text-3xl mb-4">Visualization</h1>
        <div className="flex items-center gap-3 text-sm">
          <span className="loading loading-spinner" />
          <span>
            Loading&hellip;{' '}
            <span className="opacity-70">
              {`project: ${projectReady ? '✓' : '…'} · samples: ${samplesReady ? '✓' : '…'} · orders: ${ordersReady ? '✓' : '…'}`}
            </span>
          </span>
        </div>
      </div>
    )
  }

  if (!hydrated) return null

  return <VizWorkspace orderOrProjectId={id} resumable={resumable} />
}
